import { OpcodesBCH, encodeDataPush, hexToBin, disassembleBytecodeBCH, flattenBinArray, encodeAuthenticationInstructions, decodeAuthenticationInstructions, } from '@bitauth/libauth';
import { decodeInt, encodeInt } from './data.js';
import OptimisationsEquivFile from './cashproof-optimisations.js';
export const Op = OpcodesBCH;
export function scriptToAsm(script) {
    return bytecodeToAsm(scriptToBytecode(script));
}
// TODO: See note at bytecodeToAsm
export function scriptToBitAuthAsm(script) {
    return bytecodeToBitAuthAsm(scriptToBytecode(script));
}
// TODO: Figure out why OP_0 is decoded to an empty Uint8Array (while e.g. OP_3 is just OP_3)
export function asmToScript(asm) {
    return bytecodeToScript(asmToBytecode(asm));
}
// asmToBytecode also works for BitAuth ASM
export function bitAuthAsmToScript(asm) {
    return asmToScript(asm);
}
export function scriptToBytecode(script) {
    // Convert the script elements to AuthenticationInstructions
    const instructions = script.map((opOrData) => {
        if (typeof opOrData === 'number') {
            return { opcode: opOrData };
        }
        return decodeAuthenticationInstructions(encodeDataPush(opOrData))[0];
    });
    // Convert the AuthenticationInstructions to bytecode
    return encodeAuthenticationInstructions(instructions);
}
export function bytecodeToScript(bytecode) {
    // Convert the bytecode to AuthenticationInstructions
    const instructions = decodeAuthenticationInstructions(bytecode);
    // Convert the AuthenticationInstructions to script elements
    const script = instructions.map((instruction) => ('data' in instruction ? instruction.data : instruction.opcode));
    return script;
}
export function asmToBytecode(asm) {
    // Remove any duplicate whitespace
    asm = asm.replace(/\s+/g, ' ').trim();
    // Convert the ASM tokens to AuthenticationInstructions
    const instructions = asm.split(' ').map((token) => {
        if (token.startsWith('OP_')) {
            return { opcode: Op[token] };
        }
        const data = token.replace(/<|>/g, '').replace(/^0x/, '');
        return decodeAuthenticationInstructions(encodeDataPush(hexToBin(data)))[0];
    });
    // Convert the AuthenticationInstructions to bytecode
    return encodeAuthenticationInstructions(instructions);
}
export function bytecodeToAsm(bytecode) {
    // Convert the bytecode to libauth's ASM format
    let asm = disassembleBytecodeBCH(bytecode);
    // COnvert libauth's ASM format to BITBOX's
    asm = asm.replace(/OP_PUSHBYTES_[^\s]+/g, '');
    asm = asm.replace(/OP_PUSHDATA[^\s]+ [^\s]+/g, '');
    asm = asm.replace(/(^|\s)0x/g, ' ');
    // Remove any duplicate whitespace
    asm = asm.replace(/\s+/g, ' ').trim();
    return asm;
}
// TODO: If we decide to change the ASM / artifact format, we can remove this function and merge it
// with bytecodeToAsm
export function bytecodeToBitAuthAsm(bytecode) {
    // Convert the bytecode to libauth's ASM format
    let asm = disassembleBytecodeBCH(bytecode);
    // COnvert libauth's ASM format to BITBOX's
    asm = asm.replace(/OP_PUSHBYTES_[^\s]+/g, '');
    asm = asm.replace(/OP_PUSHDATA[^\s]+ [^\s]+/g, '');
    asm = asm.replace(/(^|\s)(0x\w*)/g, ' \<$2\>');
    // Remove any duplicate whitespace
    asm = asm.replace(/\s+/g, ' ').trim();
    return asm;
}
export function countOpcodes(script) {
    return script
        .filter((opOrData) => typeof opOrData === 'number')
        .filter((op) => op > Op.OP_16)
        .length;
}
export function calculateBytesize(script) {
    return scriptToBytecode(script).byteLength;
}
// For encoding OP_RETURN data (doesn't require BIP62.3 / MINIMALDATA)
export function encodeNullDataScript(chunks) {
    return flattenBinArray(chunks.map((chunk) => {
        if (typeof chunk === 'number') {
            return new Uint8Array([chunk]);
        }
        const pushdataOpcode = getPushDataOpcode(chunk);
        return new Uint8Array([...pushdataOpcode, ...chunk]);
    }));
}
function getPushDataOpcode(data) {
    const { byteLength } = data;
    if (byteLength === 0)
        return Uint8Array.from([0x4c, 0x00]);
    if (byteLength < 76)
        return Uint8Array.from([byteLength]);
    if (byteLength < 256)
        return Uint8Array.from([0x4c, byteLength]);
    throw Error('Pushdata too large');
}
/**
 * When cutting out the tx.bytecode preimage variable, the compiler does not know
 * the size of the final redeem scrip yet, because the constructor parameters still
 * need to get added. Because of this it does not know whether the VarInt is 1 or 3
 * bytes. During compilation, an OP_NOP is added at the spot where the bytecode is
 * cut out. This function replaces that OP_NOP and adds either 1 or 3 to the cut to
 * additionally cut off the VarInt.
 *
 * @param script incomplete redeem script
 * @returns completed redeem script
 */
export function replaceBytecodeNop(script) {
    const index = script.findIndex((op) => op === Op.OP_NOP);
    if (index < 0)
        return script;
    // Remove the OP_NOP
    script.splice(index, 1);
    // Retrieve size of current OP_SPLIT
    let oldCut = script[index];
    if (oldCut instanceof Uint8Array) {
        oldCut = Number(decodeInt(oldCut));
    }
    else if (oldCut === Op.OP_0) {
        oldCut = 0;
    }
    else if (oldCut >= Op.OP_1 && oldCut <= Op.OP_16) {
        oldCut -= 80;
    }
    else {
        return script;
    }
    // Update the old OP_SPLIT by adding either 1 or 3 to it
    script[index] = encodeInt(BigInt(oldCut + 1));
    const bytecodeSize = calculateBytesize(script);
    if (bytecodeSize > 252) {
        script[index] = encodeInt(BigInt(oldCut + 3));
    }
    // Minimally encode
    return asmToScript(scriptToAsm(script));
}
export function generateRedeemScript(baseScript, encodedArgs) {
    return replaceBytecodeNop([...encodedArgs, ...baseScript]);
}
export function optimiseBytecode(script, runs = 1000) {
    const optimisations = OptimisationsEquivFile
        // Split by line and filter all line comments (#)
        .split('\n')
        .map((equiv) => equiv.trim())
        .filter((equiv) => !equiv.startsWith('#'))
        // Join back the lines, and split on semicolon
        .join('')
        .split(';')
        // Parse all optimisations in .equiv file
        .map((equiv) => equiv.trim())
        .map((equiv) => equiv.split('<=>').map((part) => part.trim()))
        .filter((equiv) => equiv.length === 2);
    for (let i = 0; i < runs; i += 1) {
        const oldScript = script;
        script = replaceOps(script, optimisations);
        // Break on fixed point
        if (scriptToAsm(oldScript) === scriptToAsm(script))
            break;
    }
    return script;
}
function replaceOps(script, optimisations) {
    let asm = scriptToAsm(script);
    // Apply all optimisations in the cashproof file
    optimisations.forEach(([pattern, replacement]) => {
        asm = asm.replace(new RegExp(pattern, 'g'), replacement);
    });
    // Add optimisations that are not compatible with CashProof
    // CashProof can't prove OP_IF without parameters
    asm = asm.replace(/OP_NOT OP_IF/g, 'OP_NOTIF');
    // CashProof can't prove OP_CHECKMULTISIG without specifying N
    asm = asm.replace(/OP_CHECKMULTISIG OP_VERIFY/g, 'OP_CHECKMULTISIGVERIFY');
    // CashProof can't prove bitwise operators
    asm = asm.replace(/OP_SWAP OP_AND/g, 'OP_AND');
    asm = asm.replace(/OP_SWAP OP_OR/g, 'OP_OR');
    asm = asm.replace(/OP_SWAP OP_XOR/g, 'OP_XOR');
    asm = asm.replace(/OP_DUP OP_AND/g, '');
    asm = asm.replace(/OP_DUP OP_OR/g, '');
    // Remove any double spaces as a result of opcode removal
    asm = asm.replace(/\s+/g, ' ').trim();
    return asmToScript(asm);
}
//# sourceMappingURL=script.js.map